import React from 'react';
import { WxbIcon } from '../Icon/Icon';
import { WxbStatusBadge, type WxbStatusTone } from '../StatusBadge/StatusBadge';
import './Statistic.css';

export type WxbStatisticTrendDirection = 'up' | 'down' | 'neutral';

export interface WxbStatisticTrend {
  direction?: WxbStatisticTrendDirection;
  label: React.ReactNode;
}

export interface WxbStatisticProps extends React.HTMLAttributes<HTMLDivElement> {
  label: React.ReactNode;
  value: React.ReactNode;
  unit?: React.ReactNode;
  description?: React.ReactNode;
  trend?: WxbStatisticTrend;
  tone?: WxbStatusTone;
}

export interface WxbMetricGridProps extends React.HTMLAttributes<HTMLDivElement> {
  columns?: 2 | 3 | 4 | 'auto';
}

const joinClasses = (...classes: Array<string | false | undefined>) =>
  classes.filter(Boolean).join(' ');

export const WxbStatistic: React.FC<WxbStatisticProps> = ({
  label,
  value,
  unit,
  description,
  trend,
  tone,
  className = '',
  ...props
}) => (
  <div className={joinClasses('wxb-statistic', tone && `wxb-statistic-${tone}`, className)} {...props}>
    <div className="wxb-statistic-label-row">
      <span className="wxb-statistic-label">{label}</span>
      {tone && <WxbStatusBadge tone={tone} variant="dot" size="sm" label="" aria-label={`${tone} metric`} />}
    </div>
    <div className="wxb-statistic-value-row">
      <span className="wxb-statistic-value">{value}</span>
      {unit && <span className="wxb-statistic-unit">{unit}</span>}
    </div>
    {(description || trend) && (
      <div className="wxb-statistic-footer">
        {trend && (
          <span className={joinClasses('wxb-statistic-trend', `wxb-statistic-trend-${trend.direction ?? 'neutral'}`)}>
            <WxbIcon name={trend.direction === 'down' ? 'deviation' : trend.direction === 'up' ? 'spectro' : 'hold-time'} size={14} />
            <span>{trend.label}</span>
          </span>
        )}
        {description && <span className="wxb-statistic-desc">{description}</span>}
      </div>
    )}
  </div>
);

export const WxbMetricGrid: React.FC<WxbMetricGridProps> = ({
  columns = 'auto',
  className = '',
  children,
  ...props
}) => (
  <div
    className={joinClasses(
      'wxb-metric-grid',
      `wxb-metric-grid-${columns}`,
      className,
    )}
    {...props}
  >
    {children}
  </div>
);
